"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Menu, User, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function OverlayMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const reduce = useReducedMotion();
  if (loading) {
    return <div className="flex flex-col items-center gap-6">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-10 w-48 animate-pulse rounded-md" style={{
        backgroundColor: "hsla(38,22%,92%,0.06)"
      }} />)}
      </div>;
  }
  const items = menuItems && menuItems.length > 0 ? menuItems : [{
    id: "home",
    label: "Home",
    href: "https://inspired-chamois-1.cynergiasites.com/"
  }, {
    id: "services",
    label: "Services",
    href: "https://inspired-chamois-1.cynergiasites.com/services-servicios/"
  }, {
    id: "gallery",
    label: "Gallery",
    href: "https://inspired-chamois-1.cynergiasites.com/gallery-galeria/"
  }, {
    id: "about",
    label: "About",
    href: "https://inspired-chamois-1.cynergiasites.com/about-nosotros/"
  }, {
    id: "contact",
    label: "Contact",
    href: "https://inspired-chamois-1.cynergiasites.com/contact-contacto/"
  }, {
    id: "faq",
    label: "FAQ",
    href: "https://inspired-chamois-1.cynergiasites.com/contact-contacto/#common-questions-preguntas-comunes"
  }];
  const displayItems = items;
  return <nav aria-label="Primary" className="flex w-full flex-col items-start px-12 md:items-center md:px-0">
      <ul className="flex flex-col items-start gap-6 md:items-center md:gap-8">
        {displayItems.map((item, i) => <motion.li key={item.id} data-index={i} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 32
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 24
      }} transition={{
        duration: reduce ? 0.2 : 0.5,
        delay: reduce ? 0 : 0.15 + i * 0.06,
        ease: [0.2, 0.7, 0.1, 1]
      }} className="overflow-hidden">
            <Link to={item.href || "#"} onClick={onClose} className="font-serif text-4xl font-light tracking-tight text-foreground transition-colors duration-300 hover:text-primary md:text-6xl lg:text-7xl" style={{
          lineHeight: 1.06
        }}>
              {item.label}
            </Link>
          </motion.li>)}
      </ul>
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const reduce = useReducedMotion();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="28690" id="valero-elite-header" data-nav="dark" className="fixed inset-x-0 top-0 z-50 bg-transparent text-foreground" style={{
    paddingTop: "20px"
  }} aria-label="Site header">
      <div className="mx-auto flex items-center justify-between" style={{
      marginLeft: "clamp(16px, 4vw, 72px)",
      marginRight: "clamp(16px, 4vw, 72px)",
      height: "52px",
      borderRadius: "999px",
      paddingLeft: "clamp(20px, 2vw, 28px)",
      paddingRight: "clamp(12px, 1.4vw, 20px)",
      backgroundColor: "hsla(30,6%,6%,0.55)",
      backdropFilter: "blur(18px) saturate(140%)",
      WebkitBackdropFilter: "blur(18px) saturate(140%)",
      border: "1px solid hsla(38,22%,92%,0.08)",
      boxShadow: scrolled ? "0 20px 60px -30px hsla(0,0%,0%,0.6)" : "0 8px 30px -20px hsla(0,0%,0%,0.5)",
      transition: "box-shadow 400ms ease, height 300ms ease"
    }}><Link to="https://inspired-chamois-1.cynergiasites.com/" className="font-serif font-light text-foreground" style={{
        fontSize: "clamp(11px, 3.2vw, 18px)",
        letterSpacing: "clamp(0.08em, 1.2vw, 0.18em)",
        lineHeight: 1
      }} aria-label="Valero Elite — Home">
                    VALERO ELITE DETAILING
                  </Link><div className="flex items-center" style={{
        gap: '18px'
      }}><Link to="https://inspired-chamois-1.cynergiasites.com/contact-contacto/" aria-label="Account" className="hidden items-center justify-center text-foreground/85 transition-colors duration-300 hover:text-primary md:inline-flex" style={{
          width: '32px',
          height: '32px'
        }}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-user h-5 w-5" aria-hidden="true"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg></Link><Button variant="ghost" size="icon" aria-label="Open menu" aria-expanded={menuOpen} aria-controls="site-menu-overlay" onClick={() => setMenuOpen(!menuOpen)} className="size-9 rounded-lg bg-transparent text-foreground hover:bg-transparent hover:text-primary focus-visible:ring-1" style={{
          width: '36px',
          height: '36px'
        }}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-menu h-6 w-6" aria-hidden="true"><path d="M4 5h16"></path><path d="M4 12h16"></path><path d="M4 19h16"></path></svg></Button></div></div>

      <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
        <DialogContent id="site-menu-overlay" showCloseButton={false} className="left-0 top-0 grid h-screen w-screen max-w-none translate-x-0 translate-y-0 gap-0 border-0 bg-transparent p-0 sm:max-w-none">
          <DialogTitle className="sr-only">Site navigation menu</DialogTitle>

          <AnimatePresence mode="wait">
            {menuOpen && <motion.div key="overlay" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: reduce ? 0.15 : 0.35,
            ease: "easeOut"
          }} className="relative flex h-full w-full items-center justify-center !bg-[url(https://valeroelitedetailingmiami.com/wp-content/uploads/2026/07/2c5adb21-78b8-414e-bdd9-7f8da4aa1a32.jpeg)] bg-cover bg-center !bg-[url(https://valeroelitedetailingmiami.com/wp-content/uploads/2026/07/detailing_ceramic_coating.png)] !bg-[url(https://valeroelitedetailingmiami.com/wp-content/uploads/2026/07/detailing_ceramic_coating.png)]" style={{
            backgroundColor: "hsla(30,6%,6%,0.96)",
            backdropFilter: "blur(24px) saturate(140%)",
            WebkitBackdropFilter: "blur(24px) saturate(140%)"
          }}>
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                  <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice">
                    <g fill="none" stroke="hsl(26 4% 16%)" strokeWidth="1" opacity="0.35">
                      <path d="M0,720 C300,660 600,780 900,700 C1200,620 1400,760 1600,700" />
                      <path d="M0,680 C300,620 600,740 900,660 C1200,580 1400,720 1600,660" />
                      <path d="M0,640 C300,580 600,700 900,620 C1200,540 1400,680 1600,620" />
                      <path d="M0,600 C300,540 600,660 900,580 C1200,500 1400,640 1600,580" />
                      <path d="M0,560 C300,500 600,620 900,540 C1200,460 1400,600 1600,540" />
                    </g>
                  </svg>
                </div>

                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3" style={{
              background: "radial-gradient(60% 50% at 50% 90%, hsla(38,48%,58%,0.15) 0%, hsla(38,48%,58%,0) 70%)",
              filter: "blur(40px)"
            }} />

                <div className="absolute flex items-center justify-between" style={{
              top: "20px",
              left: "clamp(16px, 4vw, 72px)",
              right: "clamp(16px, 4vw, 72px)",
              height: "52px"
            }}>
                  <Link to="/" onClick={() => setMenuOpen(false)} className="font-serif font-light text-foreground" style={{
                fontSize: "clamp(11px, 3.2vw, 18px)",
                letterSpacing: "clamp(0.08em, 1.2vw, 0.18em)",
                lineHeight: 1
              }} aria-label="Valero Elite — Home">
                    VALERO ELITE DETAILING
                  </Link>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="rounded-lg bg-transparent text-foreground hover:bg-transparent hover:text-primary" style={{
                  width: "36px",
                  height: "36px"
                }}>
                      <X strokeWidth={1.25} className="h-6 w-6" />
                    </Button>
                  </div>
                </div>

                <div className="relative z-10 w-full">
                  <MenuProvider menu_id="2">
                    <OverlayMenuConsumer onClose={() => setMenuOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="absolute bottom-0 left-0 right-0 flex flex-col items-start justify-between gap-4 border-t px-12 py-8 md:flex-row md:items-center" style={{
              borderColor: "hsl(26 4% 16%)"
            }}>
                  <span className="font-sans uppercase text-muted-foreground" style={{
                fontSize: "12px",
                letterSpacing: "0.22em"
              }}>
                    valeroelitedetailing@gmail.com
                  </span>
                  <span className="font-sans uppercase text-muted-foreground" style={{
                fontSize: "12px",
                letterSpacing: "0.22em"
              }}>
                    Miami, Florida
                  </span>
                </div>
              </motion.div>}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </section>;
}